import { useState, useEffect, useCallback } from 'react';
import { AuthState, LoginCredentials, SignupData } from '../types';
import { authApiService } from '../services/authApi';
import { AUTH_ERROR_MESSAGES } from '../constants';

export const useAuth = () => {
    const [state, setState] = useState<AuthState>({
        user: null,
        token: null,
        isLoading: true,
        isAuthenticated: false,
        error: null,
    });

    // Initialize auth state on mount
    useEffect(() => {
        const initAuth = async () => {
            const storedUser = authApiService.getStoredUser();
            const isAuthenticated = authApiService.isAuthenticated();

            if (isAuthenticated && storedUser) {
                setState(prev => ({
                    ...prev,
                    user: storedUser,
                    isAuthenticated: true,
                    isLoading: false,
                }));
            } else {
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                }));
            }
        };

        initAuth();
    }, []);

    const login = useCallback(async (credentials: LoginCredentials) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            const response = await authApiService.login(credentials);
            
            setState(prev => ({
                ...prev,
                user: response.user,
                token: response.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            }));

            return response;
        } catch (error) {
            let errorMessage = AUTH_ERROR_MESSAGES.SERVER_ERROR;
            
            if (error instanceof Error) {
                if (error.message === 'INVALID_CREDENTIALS') {
                    errorMessage = AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS;
                } else if (error.message === 'INCORRECT_PASSWORD') {
                    errorMessage = AUTH_ERROR_MESSAGES.INCORRECT_PASSWORD;
                }
            }

            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));

            throw error;
        }
    }, []);

    const register = useCallback(async (userData: SignupData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
        const response = await authApiService.register(userData);

        // Only set isAuthenticated if we have a valid token
        // Candidate signup returns empty token (admin approval required)
        const hasValidToken = response.token && response.token.length > 0;

        setState(prev => ({
            ...prev,
            user: response.user,
            token: response.token,
            isAuthenticated: hasValidToken,
            isLoading: false,
            error: null,
        }));

        return response;
    } catch (error) {
        let errorMessage: string = AUTH_ERROR_MESSAGES.SERVER_ERROR;
        
        if (error instanceof Error) {
            if (error.message === 'EMAIL_ALREADY_EXISTS') {
                errorMessage = AUTH_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
            }
        }
        
        setState(prev => ({
            ...prev,
            isLoading: false,
            error: errorMessage,
        }));

        throw error;
    }
    }, []);


    const logout = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true }));

        try {
            await authApiService.logout();
            
            setState({
                user: null,
                token: null,
                isLoading: false,
                isAuthenticated: false,
                error: null,
            });
        } catch (error) {
            console.error('Logout failed:', error);
            // Still clear state even if API call fails
            setState({
                user: null,
                token: null,
                isLoading: false,
                isAuthenticated: false,
                error: null,
            });
        }
    }, []);

    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);

    // const oauthLogin = useCallback(async (provider: 'google' | 'linkedin', token: string) => {
    //     setState(prev => ({ ...prev, isLoading: true, error: null }));

    //     try {
    //         const response = await authApiService.oauthLogin(provider, token);
            
    //         setState(prev => ({
    //             ...prev,
    //             user: response.user,
    //             token: response.token,
    //             isAuthenticated: true,
    //             isLoading: false,
    //             error: null,
    //         }));

    //         return response;
    //     } catch (error) {
    //         setState(prev => ({
    //             ...prev,
    //             isLoading: false,
    //             error: error instanceof Error ? error.message : AUTH_ERROR_MESSAGES.SERVER_ERROR,
    //         }));

    //         throw error;
    //     }
    // }, []);

    return {
        ...state,
        login,
        register,
        logout,
        clearError,
        // oauthLogin,
    };
};
